/**
 * Design system component: Avatar
 *
 * Circular initial-letter avatar for group members. Renders the first
 * character of `name` in a coloured circle. Three sizes are supported
 * (26px for compact rows, 34px for standard nodes, 38px for larger profiles).
 *
 * When `isCurrentUser` is true the background uses the accent colour with
 * white text so the current user stands out in possession chains and member
 * lists. Otherwise a caller-supplied `bg` colour (typically the category
 * palette colour) or `surfaceSubtle` is used.
 */
import { memo } from 'react';
import { T } from '@/lib/tokens';

interface AvatarProps {
  name: string;
  size?: 26 | 34 | 38;
  bg?: string;
  isCurrentUser?: boolean;
}

export const Avatar = memo(function Avatar({
  name,
  size = 34,
  bg,
  isCurrentUser = false,
}: AvatarProps) {
  const background = isCurrentUser ? T.accent : (bg ?? T.surfaceSubtle);
  const textColour = isCurrentUser ? T.white : T.textSec;
  const initial = (name?.[0] ?? '?').toUpperCase();

  return (
    <div
      aria-label={name}
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        flexShrink: 0,
        background: background,
        border: `1.5px solid ${T.border}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: Math.round(size * 0.38),
        fontWeight: 700,
        color: textColour,
      }}
    >
      {initial}
    </div>
  );
});
